import { useState, type ComponentProps } from "react"
import { Copy } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { DEFAULT_DUPLICATE_SECTIONS, DUPLICATE_SECTIONS } from "@/lib/duplicate"
import { useReadOnly } from "@/store/session"

const READ_ONLY_TITLE = "Суб'єкт виключений з реєстру — доступний лише перегляд"

/**
 * design-system.md's `DuplicateDialog` — pick which sections of an existing
 * document ОТК to carry into a new draft. Selection resets to
 * `DEFAULT_DUPLICATE_SECTIONS` on close.
 */
export function DuplicateDialog({
  onConfirm,
  triggerLabel = "Дублювати",
  triggerSize,
  triggerVariant = "outline",
}: {
  onConfirm: (sectionIds: string[]) => void | Promise<void>
  triggerLabel?: string
  triggerSize?: ComponentProps<typeof Button>["size"]
  triggerVariant?: ComponentProps<typeof Button>["variant"]
}) {
  const readOnly = useReadOnly()
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<string[]>(DEFAULT_DUPLICATE_SECTIONS)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) setError(null)
    if (!nextOpen) setSelected(DEFAULT_DUPLICATE_SECTIONS)
    setOpen(nextOpen)
  }

  async function handleConfirm() {
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await onConfirm(selected)
      handleOpenChange(false)
    } catch {
      setError("Не вдалося дублювати документ. Спробуйте ще раз.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogTrigger asChild>
        <Button
          disabled={readOnly}
          size={triggerSize}
          title={readOnly ? READ_ONLY_TITLE : undefined}
          variant={triggerVariant}
        >
          <Copy data-icon="inline-start" />
          <span className="max-lg:sr-only">{triggerLabel}</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Дублювати документ</DialogTitle>
          <DialogDescription>
            Оберіть розділи, які потрібно перенести до нового чернетки.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {DUPLICATE_SECTIONS.map((section) => {
            // Photos live in per-document blob storage — copying them needs
            // fetching each source file and re-uploading it to the new
            // draft, which the existing endpoints don't do in one call.
            // Disabled rather than silently ignored if checked, so the gap
            // is visible instead of a section quietly not being copied.
            const unsupported = section.id === "photos"
            return (
              <label className="flex items-start gap-3" key={section.id}>
                <Checkbox
                  checked={!unsupported && selected.includes(section.id)}
                  className="mt-0.5"
                  disabled={unsupported}
                  onCheckedChange={(checked) => {
                    setSelected((current) =>
                      checked === true
                        ? [...current, section.id]
                        : current.filter((id) => id !== section.id)
                    )
                  }}
                />
                <span>
                  <span className="block text-sm leading-none font-medium">
                    {section.name}
                  </span>
                  <span className="block text-sm text-muted-foreground">
                    {unsupported ? "Поки не підтримується" : section.hint}
                  </span>
                </span>
              </label>
            )
          })}
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button disabled={submitting} onClick={handleConfirm}>
            {submitting ? "Дублювання…" : "Створити дублікат"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
